import { useEffect, useState } from "react";
import { api, downloadFile, CATEGORY_LABELS } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { FileDown, FileSpreadsheet, FileText } from "lucide-react";
import { toast } from "sonner";

const fmtQ = (v) => Number(v || 0).toLocaleString("id-ID", { maximumFractionDigits: 4 });

function ChecklistSummary({ insp }) {
  if (!insp?.results) return <span className="text-muted-foreground">Belum diperiksa</span>;
  const v = Object.values(insp.results);
  const n = (k) => v.filter((x) => x === k).length;
  return (
    <span className="text-xs whitespace-nowrap">
      <span className="text-emerald-700">✓ {n("memenuhi")}</span> · <span className="text-red-700">✗ {n("tidak_memenuhi")}</span> · <span className="text-slate-500">N/A {n("tidak_berlaku")}</span>
    </span>
  );
}

function StockSummary({ lots }) {
  const sum = (st) => lots.filter((l) => l.status === st && !l.expired).reduce((a, l) => a + (l.qty_remaining || 0), 0);
  const unit = lots[0]?.unit || "";
  const items = [["Stok tersedia", sum("DITERIMA"), "text-emerald-700", "rep-stock-available"], ["Karantina", sum("KARANTINA"), "text-amber-700", "rep-stock-quarantine"],
    ["Lot aktif", lots.filter((l) => l.status === "DITERIMA" && l.qty_remaining > 0).length, "text-slate-700", "rep-stock-lots"]];
  return (
    <div className="grid grid-cols-3 gap-3">
      {items.map(([lbl, val, cls, tid], i) => (
        <div key={lbl} className="rounded-lg border p-3"><div className="text-xs text-muted-foreground">{lbl}</div>
          <div data-testid={tid} className={`font-heading text-xl font-extrabold ${cls}`}>{fmtQ(val)} {i < 2 ? unit : ""}</div></div>
      ))}
    </div>
  );
}

function ReceiptTable({ rows }) {
  const heads = ["Tgl Terima", "Pemasok", "Batch Internal", "No. Tracking", "Jumlah", "Checklist", "Status"];
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border" data-testid="rep-receipt-table">
        <thead className="bg-muted"><tr>{heads.map((h) => <th key={h} className="border px-2 py-2 text-xs font-bold text-left">{h}</th>)}</tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-muted-foreground" data-testid="rep-empty">Tidak ada penerimaan untuk filter ini.</td></tr>}
          {rows.map((r) => (
            <tr key={r.id} data-testid={`rep-row-${r.internal_batch}`}>
              <td className="border px-2 py-1.5">{r.tgl_terima}</td>
              <td className="border px-2">{r.supplier_code} — {r.supplier_name}</td>
              <td className="border px-2 font-mono text-xs">{r.internal_batch}</td>
              <td className="border px-2 font-mono text-xs">{r.tracking_no}</td>
              <td className="border px-2">{fmtQ(r.jumlah)} {r.unit}</td>
              <td className="border px-2"><ChecklistSummary insp={r.inspection} /></td>
              <td className="border px-2"><StatusBadge status={r.status} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Reports() {
  const [suppliers, setSuppliers] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [f, setF] = useState({ date_from: "", date_to: "", supplier_id: "all", material_id: "all", status: "all", category: "all" });
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    api.get("/suppliers").then((r) => setSuppliers(r.data));
    api.get("/materials").then((r) => setMaterials(r.data));
  }, []);

  useEffect(() => {
    if (f.material_id === "all") { setPreview(null); return; }
    const params = { with_inspection: true };
    Object.entries(f).forEach(([k, v]) => { if (v && v !== "all") params[k] = v; });
    Promise.all([api.get("/receipts", { params }), api.get("/stock")])
      .then(([r, s]) => setPreview({ rows: r.data, lots: s.data.filter((l) => l.material_id === f.material_id) }))
      .catch(() => toast.error("Gagal memuat data."));
  }, [f]);

  function qs() {
    const p = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => { if (v && v !== "all") p.append(k, v); });
    return p.toString();
  }

  async function dl(kind) {
    const q = qs();
    try {
      if (kind === "pdf") await downloadFile(`/reports/mj-form-01.pdf?${q}`, "MJ-FORM-01_Laporan.pdf");
      if (kind === "xlsx") await downloadFile(`/reports/export.xlsx?${q}`, "MJ-FORM-01_Rekap.xlsx");
      if (kind === "csv") await downloadFile(`/reports/export.csv?${q}`, "MJ-FORM-01_Data.csv");
      toast.success("File berhasil diunduh.");
    } catch { toast.error("Gagal mengunduh file."); }
  }

  return (
    <div className="space-y-5">
      <div><h1 className="font-heading text-2xl font-extrabold">MJ-FORM-01 — Laporan</h1>
        <p className="text-sm text-muted-foreground">Pilih bahan untuk langsung melihat daftar penerimaan dan stoknya. Unduh formulir resmi (PDF), rekap Excel, dan CSV.</p></div>

      <Card className="p-5 space-y-4">
        <h4 className="font-heading font-bold text-sm">Filter Laporan Periode</h4>
        <div className="grid sm:grid-cols-3 gap-4">
          <div><Label className="text-xs">Dari Tanggal</Label><Input type="date" data-testid="rep-from" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} className="mt-1" /></div>
          <div><Label className="text-xs">Sampai Tanggal</Label><Input type="date" data-testid="rep-to" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} className="mt-1" /></div>
          <div><Label className="text-xs">Status QC</Label>
            <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}><SelectTrigger data-testid="rep-status" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Semua</SelectItem>{["DRAFT","MENUNGGU_PEMERIKSAAN","KARANTINA","DITERIMA","DITOLAK","DIBATALKAN"].map((s) => <SelectItem key={s} value={s}>{s.replace("_"," ")}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-xs">Pemasok</Label>
            <Select value={f.supplier_id} onValueChange={(v) => setF({ ...f, supplier_id: v })}><SelectTrigger data-testid="rep-supplier" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Semua</SelectItem>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-xs">Bahan</Label>
            <Select value={f.material_id} onValueChange={(v) => setF({ ...f, material_id: v })}><SelectTrigger data-testid="rep-material" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Semua</SelectItem>{materials.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-xs">Kategori</Label>
            <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}><SelectTrigger data-testid="rep-category" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Semua</SelectItem>{Object.entries(CATEGORY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
        </div>
        <div className="flex flex-wrap gap-3 pt-2">
          <Button data-testid="dl-pdf" onClick={() => dl("pdf")}><FileDown className="w-4 h-4 mr-1" /> Download Formulir MJ-FORM-01 (PDF)</Button>
          <Button variant="outline" data-testid="dl-xlsx" onClick={() => dl("xlsx")}><FileSpreadsheet className="w-4 h-4 mr-1" /> Excel XLSX</Button>
          <Button variant="outline" data-testid="dl-csv" onClick={() => dl("csv")}><FileText className="w-4 h-4 mr-1" /> CSV</Button>
        </div>
      </Card>

      {preview && (
        <Card className="p-5 space-y-4" data-testid="rep-preview">
          <h4 className="font-heading font-bold text-sm">{materials.find((m) => m.id === f.material_id)?.name} — {preview.rows.length} penerimaan</h4>
          <StockSummary lots={preview.lots} />
          <ReceiptTable rows={preview.rows} />
        </Card>
      )}

      <Card className="p-5">
        <h4 className="font-heading font-bold text-sm mb-2">Catatan Format</h4>
        <ul className="text-sm text-muted-foreground list-disc pl-5 space-y-1">
          <li>PDF meniru formulir MJ-FORM-01 (A4 landscape): header identitas, area logo, info Dibuat/Diperiksa/Disetujui, header tabel bertingkat dengan sel gabungan, seluruh kolom checklist, status, dan paraf.</li>
          <li>Mode per transaksi tersedia di halaman detail penerimaan (tombol "Download Formulir MJ-FORM-01").</li>
          <li>Excel memuat hasil checklist setiap kriteria, bukan hanya data ringkas dashboard.</li>
        </ul>
      </Card>
    </div>
  );
}
